import React, { useEffect, useMemo, useState } from "react";
import "../../styles/components/layout/BackgroundLayer.css";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

function createItems(count, seed, mapper) {
  const rng = seededRandom(seed);
  return Array.from({ length: count }, (_, i) => mapper(rng, i));
}

export default function BackgroundLayer() {
  const [isMobile, setIsMobile] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [isLowPower, setIsLowPower] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const updateEnv = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      setIsReducedMotion(motionQuery.matches);

      const lowPowerDevice =
        mobile ||
        (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4);
      setIsLowPower(lowPowerDevice);
    };

    updateEnv();

    const motionHandler = () => updateEnv();
    window.addEventListener("resize", updateEnv);
    motionQuery.addEventListener?.("change", motionHandler);

    return () => {
      window.removeEventListener("resize", updateEnv);
      motionQuery.removeEventListener?.("change", motionHandler);
    };
  }, []);

  const config = useMemo(() => {
    if (isReducedMotion) {
      return {
        rays: 4,
        plankton: 18,
        debris: 8,
        bubbles: 0,
        caustics: 2,
        fog: 2,
        sonar: 1,
      };
    }

    if (isLowPower) {
      return {
        rays: 6,
        plankton: 24,
        debris: 10,
        bubbles: 5,
        caustics: 2,
        fog: 2,
        sonar: 2,
      };
    }

    return {
      rays: 8,
      plankton: 40,
      debris: 14,
      bubbles: 8,
      caustics: 3,
      fog: 3,
      sonar: 2,
    };
  }, [isLowPower, isReducedMotion]);

  const rays = useMemo(() => {
    return createItems(config.rays, 42, (rng, i) => ({
      id: i,
      left: 6 + i * (80 / Math.max(config.rays - 1, 1)) + rng() * 2.5,
      rotate: -26 + i * 6 + rng() * 4,
      delay: i * 0.45,
      duration: 10 + i * 0.65 + rng() * 1.2,
      width: 10 + rng() * 6,
      opacity: 0.12 + rng() * 0.12,
    }));
  }, [config.rays]);

  const plankton = useMemo(() => {
    return createItems(config.plankton, 77, (rng, i) => ({
      id: `p${i}`,
      left: rng() * 100,
      top: rng() * 120,
      size: (rng() * 2 + 1.1).toFixed(2),
      duration: (rng() * 14 + 18).toFixed(2),
      delay: (rng() * 12).toFixed(2),
      driftX: (rng() * 34 - 17).toFixed(2),
      rise: (rng() * 46 + 72).toFixed(2),
      opacity: (rng() * 0.18 + 0.08).toFixed(2),
      glow: rng() > 0.62 ? 1 : 0,
    }));
  }, [config.plankton]);

  const debris = useMemo(() => {
    return createItems(config.debris, 113, (rng, i) => ({
      id: `d${i}`,
      left: rng() * 100,
      top: rng() * 100,
      size: (rng() * 1.6 + 0.7).toFixed(2),
      duration: (rng() * 22 + 34).toFixed(2),
      delay: (rng() * 14).toFixed(2),
      driftX: (rng() * 34 - 17).toFixed(2),
      driftY: (rng() * 18 - 9).toFixed(2),
      opacity: (rng() * 0.08 + 0.03).toFixed(2),
    }));
  }, [config.debris]);

  const bubbles = useMemo(() => {
    return createItems(config.bubbles, 199, (rng, i) => ({
      id: `b${i}`,
      left: 8 + rng() * 84,
      top: 58 + rng() * 45,
      size: (rng() * 4 + 2.2).toFixed(2),
      duration: (rng() * 6 + 7).toFixed(2),
      delay: (rng() * 12).toFixed(2),
      driftX: (rng() * 18 - 9).toFixed(2),
      wobble: (rng() * 8 + 4).toFixed(2),
    }));
  }, [config.bubbles]);

  const caustics = useMemo(() => {
    const all = [
      {
        id: "c1",
        left: "8%",
        top: "-4%",
        width: "40vw",
        height: "24vh",
        rotate: "-10deg",
        delay: "0s",
        duration: "16s",
        strength: 1,
      },
      {
        id: "c2",
        left: "38%",
        top: "-6%",
        width: "30vw",
        height: "20vh",
        rotate: "4deg",
        delay: "2.2s",
        duration: "18s",
        strength: 0.76,
      },
      {
        id: "c3",
        left: "66%",
        top: "-2%",
        width: "30vw",
        height: "18vh",
        rotate: "12deg",
        delay: "4s",
        duration: "14s",
        strength: 0.58,
      },
    ];
    return all.slice(0, config.caustics);
  }, [config.caustics]);

  const fogLayers = useMemo(() => {
    const all = [
      {
        id: "f1",
        top: "24%",
        opacity: 0.05,
        blur: 26,
        duration: "24s",
        delay: "0s",
        scaleX: 1,
      },
      {
        id: "f2",
        top: "50%",
        opacity: 0.08,
        blur: 40,
        duration: "30s",
        delay: "6s",
        scaleX: 1.12,
      },
      {
        id: "f3",
        top: "74%",
        opacity: 0.1,
        blur: 52,
        duration: "36s",
        delay: "12s",
        scaleX: 0.96,
      },
    ];
    return all.slice(0, config.fog);
  }, [config.fog]);

  const sonarRings = useMemo(() => {
    const all = [
      {
        id: "s1",
        size: isMobile ? 180 : 240,
        left: "10%",
        bottom: "12%",
        top: "auto",
        right: "auto",
        delay: "0s",
        duration: "11s",
      },
      {
        id: "s2",
        size: isMobile ? 220 : 300,
        right: "8%",
        top: "22%",
        bottom: "auto",
        left: "auto",
        delay: "4.2s",
        duration: "13s",
      },
    ];
    return all.slice(0, config.sonar);
  }, [config.sonar, isMobile]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const root = document.documentElement;
    let rafId = 0;
    let lastScrollY = -1;

    const updateEffects = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;

      if (scrollY === lastScrollY) {
        rafId = 0;
        return;
      }

      lastScrollY = scrollY;

      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPct = maxScroll > 0 ? scrollY / maxScroll : 0;

      const shiftPx = Math.round(scrollPct * window.innerHeight * (isMobile ? 2.4 : 3.6));
      const depthDark = clamp(scrollPct * 0.55, 0, 0.55);
      const topLight = clamp(1 - scrollPct * 2.1, 0, 1);
      const causticOp = clamp(0.8 - scrollPct * 1.3, 0.18, 0.8);
      const sonarOp = clamp(0.1 - scrollPct * 0.04, 0.04, 0.1);
      const hazeOp = clamp(0.18 + scrollPct * 0.16, 0.18, 0.34);
      const planktonOp = clamp(0.88 + scrollPct * 0.1, 0.88, 1);
      const surfaceShimmer = clamp(1 - scrollPct * 2.6, 0.22, 1);

      root.style.setProperty("--ocean-shift", `${shiftPx}px`);
      root.style.setProperty("--depth-dark", depthDark.toFixed(3));
      root.style.setProperty("--top-light", topLight.toFixed(3));
      root.style.setProperty("--caustic-opacity", causticOp.toFixed(3));
      root.style.setProperty("--sonar-opacity", sonarOp.toFixed(3));
      root.style.setProperty("--depth-haze-opacity", hazeOp.toFixed(3));
      root.style.setProperty("--plankton-opacity", planktonOp.toFixed(3));
      root.style.setProperty("--surface-shimmer", surfaceShimmer.toFixed(3));

      rafId = 0;
    };

    const onScroll = () => {
      if (!rafId) {
        rafId = window.requestAnimationFrame(updateEffects);
      }
    };

    const onResize = () => {
      if (!rafId) {
        rafId = window.requestAnimationFrame(updateEffects);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    updateEffects();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  return (
    <div className="ocean-root" aria-hidden="true">
      <div className="ocean">
        <div className="ocean-surface" />
        <div className="ocean-surface-glow" />

        <div className="ocean-caustics">
          {caustics.map((item) => (
            <span
              key={item.id}
              className="caustic"
              style={{
                left: item.left,
                top: item.top,
                width: item.width,
                height: item.height,
                animationDelay: item.delay,
                animationDuration: item.duration,
                "--caustic-strength": item.strength,
                "--caustic-rotate": item.rotate,
              }}
            />
          ))}
        </div>

        <div className="light-rays">
          {rays.map((ray) => (
            <span
              key={ray.id}
              className="ray"
              style={{
                left: `${ray.left}%`,
                animationDelay: `${ray.delay}s`,
                animationDuration: `${ray.duration}s`,
                width: `${ray.width}vw`,
                "--ray-base-opacity": ray.opacity,
                "--ray-rotate": `${ray.rotate}deg`,
              }}
            />
          ))}
        </div>

        <div className="depth-fog">
          {fogLayers.map((f) => (
            <span
              key={f.id}
              className="fog-band"
              style={{
                top: f.top,
                "--fog-opacity": f.opacity,
                filter: `blur(${f.blur}px)`,
                animationDuration: f.duration,
                animationDelay: f.delay,
                "--fog-scale": f.scaleX,
              }}
            />
          ))}
        </div>

        <div className="sonar-layer">
          {sonarRings.map((ring) => (
            <span
              key={ring.id}
              className="sonar-ring"
              style={{
                width: `${ring.size}px`,
                height: `${ring.size}px`,
                left: ring.left || "auto",
                right: ring.right || "auto",
                top: ring.top || "auto",
                bottom: ring.bottom || "auto",
                animationDelay: ring.delay,
                animationDuration: ring.duration,
              }}
            />
          ))}
        </div>

        <div className="depth-orb depth-orb-a" />
        <div className="depth-orb depth-orb-b" />
        <div className="depth-orb depth-orb-c" />

        {!isLowPower && <div className="depth-orb depth-orb-d" />}

        <div className="particles">
          {plankton.map((p) => (
            <span
              key={p.id}
              className={`particle${p.glow ? " particle-glow" : ""}`}
              style={{
                left: `${p.left}%`,
                top: `${p.top}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
                "--x-drift": `${p.driftX}px`,
                "--rise": `${p.rise}vh`,
                "--particle-opacity": p.opacity,
              }}
            />
          ))}
        </div>

        <div className="debris-layer">
          {debris.map((d) => (
            <span
              key={d.id}
              className="debris"
              style={{
                left: `${d.left}%`,
                top: `${d.top}%`,
                width: `${d.size}px`,
                height: `${d.size}px`,
                animationDuration: `${d.duration}s`,
                animationDelay: `${d.delay}s`,
                "--dx": `${d.driftX}px`,
                "--dy": `${d.driftY}px`,
                "--debris-opacity": d.opacity,
              }}
            />
          ))}
        </div>

        {!!bubbles.length && (
          <div className="bubble-layer">
            {bubbles.map((b) => (
              <span
                key={b.id}
                className="bubble"
                style={{
                  left: `${b.left}%`,
                  top: `${b.top}%`,
                  width: `${b.size}px`,
                  height: `${b.size}px`,
                  animationDuration: `${b.duration}s`,
                  animationDelay: `${b.delay}s`,
                  "--bx": `${b.driftX}px`,
                  "--wobble": `${b.wobble}px`,
                }}
              />
            ))}
          </div>
        )}

        <div className="ocean-vignette" />
      </div>
    </div>
  );
}